import React from 'react';
import { ago } from '../three/ecosystemState';
import { fmtRate } from './telemetryState';
import { openAnomalies } from './anomaly';

// HUD panels shared by the fleet / org / app command views: live traffic
// totals and the anomaly list. Both read only the normalised telemetry.
const Row = ({ k, v }) => <span className="cmd-row"><span>{k}</span><span>{v}</span></span>;
const n = (v) => (v == null ? '—' : Math.round(v).toLocaleString());

export function trafficPanel(t, { side = 'right', id = 'traffic' } = {}) {
  const open = openAnomalies(t.anomalies);
  const has = t.source !== 'none' && Object.keys(t.series).length > 0;
  const busiest = Object.entries(t.live).sort((a, b) => b[1].req - a[1].req).slice(0, 3);
  const rt = runtimeTotals(t);
  return {
    id, side, level: !has ? 'off' : open.some((a) => a.severity === 'down') ? 'down' : open.length ? 'warn' : 'ok',
    title: `Traffic · ${t.window.label}`,
    body: has ? (
      <>
        <strong>{fmtRate(Object.values(t.live).reduce((s, x) => s + x.req, 0))}<span className="cmd-sub"> now · {n(t.totals.req)} total</span></strong>
        <Row k="5xx" v={`${n(t.totals.s5xx)}${t.totals.req ? ` · ${((t.totals.s5xx / t.totals.req) * 100).toFixed(2)}%` : ''}`} />
        <Row k="p95 (worst)" v={t.totals.p95 ? `${n(t.totals.p95)} ms` : '—'} />
        <Row k="tokens" v={n(t.totals.tokens)} />
        <Row k="inter-app flows" v={t.flows.length} />
        {rt && <Row k="queues" v={`${rt.queued} queued · ${rt.running} running${rt.stale ? ` · ${rt.stale} stuck` : ''}`} />}
        {rt && rt.users != null && <Row k="active users today" v={n(rt.users)} />}
        {busiest.map(([slug, x]) => <Row key={slug} k={slug} v={fmtRate(x.req)} />)}
      </>
    ) : <span className="cmd-sub">No traffic data yet — the edge access log rolls up once the host agent is on this pipeline. Try Simulate.</span>,
  };
}

// Sum of the kits' runtime blocks (queues / stale claims / active users).
export function runtimeTotals(t) {
  const list = Object.values(t.runtime || {}).filter((r) => r && r.totals);
  if (!list.length) return null;
  return list.reduce((a, r) => ({ queued: a.queued + (r.totals.queued || 0), running: a.running + (r.totals.running || 0), stale: a.stale + (r.totals.stale || 0), users: (a.users || 0) + (r.activeUsersToday || 0) }), { queued: 0, running: 0, stale: 0, users: 0 });
}

// Per-instance runtime rows for a drawer.
export function RuntimeRows({ t, slug }) {
  const r = t.runtime?.[slug];
  if (!r || !r.totals) return null;
  return (
    <>
      <Row k="queues" v={`${r.totals.queued} queued · ${r.totals.running} running${r.totals.stale ? ` · ${r.totals.stale} stuck` : ''}`} />
      {Object.values(r.queues || {}).filter((q) => q.queued || q.running || q.stale).slice(0, 4).map((q) => <Row key={q.label} k={q.label} v={`${q.queued}/${q.running}${q.stale ? ` · ${q.stale} stuck` : ''}`} />)}
      {r.activeUsersToday != null && <Row k="active users" v={`${r.activeUsersToday} today · ${r.activeUsers30m || 0} last 30m`} />}
      {r.exceptionsLastHour > 0 && <Row k="exceptions (1h)" v={r.exceptionsLastHour} />}
      {r.at ? <span className="cmd-sub">runtime reported {ago(r.at)}</span> : null}
    </>
  );
}

export function anomalyPanel(t, { side = 'right', id = 'anomalies' } = {}) {
  const open = openAnomalies(t.anomalies);
  const recent = t.anomalies.filter((a) => a.closedAt != null).slice(0, 2);
  return {
    id, side, level: open.some((a) => a.severity === 'down') ? 'down' : open.length ? 'warn' : t.source === 'none' ? 'off' : 'ok',
    title: 'Anomalies',
    body: (
      <>
        <strong>{open.length}<span className="cmd-sub"> open{recent.length ? ` · ${recent.length} resolved` : ''}</span></strong>
        <span className="cmd-anomaly-list">
          {open.slice(0, 3).map((a) => <AnomalyRow key={a.id} a={a} />)}
          {!open.length && t.source !== 'none' && <span className="cmd-sub">baseline holding on every app</span>}
        </span>
      </>
    ),
  };
}

export function AnomalyRow({ a }) {
  return (
    <span className="cmd-anomaly">
      <i className={`cmd-dot is-${a.severity}`} />
      <span className="cmd-anomaly-text">{a.text}</span>
    </span>
  );
}

export function AnomalyTable({ t }) {
  if (!t.anomalies.length) {
    const floors = t.thresholds?.floors;
    const z = t.thresholds?.z ?? 3;
    const mins = t.thresholds?.consecutive ?? 3;
    const floor = floors
      ? `${floors.req} req/min, ${(floors.s5xxRate * 100).toFixed(0)}% 5xx, ${floors.p95Ms} ms p95, ${Number(floors.tokens).toLocaleString()} tokens`
      : '20 req/min, 1% 5xx, 400 ms p95, 2k tokens';
    return <p className="cs-help">Nothing flagged in this window. Flags need a robust z ≥ {z} for {mins} consecutive minutes and an absolute floor ({floor}).</p>;
  }
  return (
    <div className="cs-plan-tablewrap">
      <table>
        <thead><tr><th>App</th><th>Signal</th><th>Peak</th><th>Baseline</th><th>z</th><th>Opened</th><th>State</th></tr></thead>
        <tbody>
          {t.anomalies.map((a) => (
            <tr key={a.id}>
              <td>{a.from ? `${a.from} → ${a.slug}` : a.slug}</td>
              <td><i className={`cmd-dot is-${a.severity}`} style={{ display: 'inline-block', marginRight: 6 }} />{a.metric} {a.kind}</td>
              <td>{a.metric === '5xx' ? `${(a.value * 100).toFixed(1)}%` : n(a.value)}</td>
              <td>{a.metric === '5xx' ? `${(a.baseline * 100).toFixed(2)}%` : n(a.baseline)}</td>
              <td>{a.z || '—'}</td>
              <td>{ago(a.openedAt)}</td>
              <td>{a.closedAt == null ? 'open' : `closed ${ago(a.closedAt)}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FlowTable({ t }) {
  if (!t.flows.length) return <p className="cs-help">No inter-app traffic observed in this window.</p>;
  return (
    <div className="cs-plan-tablewrap">
      <table>
        <thead><tr><th>From</th><th>To</th><th>Kind</th><th>Rate</th></tr></thead>
        <tbody>{t.flows.map((f) => <tr key={`${f.from}>${f.to}`}><td>{f.from}</td><td>{f.to}</td><td>{f.kind}</td><td>{fmtRate(f.ratePerMin)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}
